/**
 * BrainSetupTile — Milestone 37.4 · persistent "Teach Orkexra Your Business" card.
 *
 * Mounts on Executive Home. Auto-hides once both brains are approved.
 * Wired to the ``/brain-missions/gate`` endpoint so the copy always
 * reflects which brain is the next best action.
 */
import { useNavigate } from "react-router-dom";
import { Brain } from "lucide-react";

import { useBrainGate } from "@/entities/brain-missions";
import { useCurrentWorkspace } from "@/entities/workspace";

const ESTIMATED_MINUTES = 12;

export function BrainSetupTile() {
  const ws = useCurrentWorkspace();
  const navigate = useNavigate();
  const gateQ = useBrainGate(ws?.id);

  if (!ws) return null;
  const gate = gateQ.data;
  if (!gate) return null;
  if (gate.business_brain_approved && gate.brand_brain_approved) return null;

  const isBusiness = !gate.business_brain_approved;
  const headline = isBusiness
    ? "Teach Orkexra Your Business"
    : "Teach Orkexra Your Brand Voice";
  const body = isBusiness
    ? "Business Brain not complete. Every campaign Akinex builds starts here — spend 10 minutes teaching it your business and everything after feels effortless."
    : "Brand Brain not complete. Business Brain is approved; now teach Akinex how your brand should sound.";
  const cta = "Continue";
  const targetRoute = isBusiness ? "/business-brain" : "/brand-brain";

  return (
    <div
      className="obsidian-card flex flex-col gap-3 rounded-2xl border border-cyan-300/25 bg-gradient-to-br from-cyan-500/[0.08] via-transparent to-violet-500/[0.06] p-5 md:p-6"
      data-testid="brain-setup-tile"
      data-mission-target={isBusiness ? "business_brain" : "brand_brain"}
    >
      <div className="flex items-center gap-2">
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-cyan-400/15 text-cyan-300">
          <Brain className="h-4 w-4" />
        </div>
        <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-cyan-300/80">
          Highest-priority recommendation
        </div>
      </div>
      <h3
        className="text-xl font-semibold text-white sm:text-2xl"
        data-testid="brain-setup-tile-headline"
      >
        {headline}
      </h3>
      <p className="max-w-2xl text-sm leading-relaxed text-white/70">
        {body}
      </p>
      <div className="mt-1 flex items-center justify-between gap-3">
        <div className="text-xs text-white/50">
          <span className="font-semibold text-white/80">Estimated time</span>{" "}
          <span data-testid="brain-setup-tile-time">{ESTIMATED_MINUTES} minutes</span>
        </div>
        <button
          type="button"
          onClick={() => navigate(targetRoute)}
          className="rounded-full bg-gradient-to-r from-cyan-400 via-sky-500 to-violet-500 px-5 py-2 text-sm font-semibold text-slate-950 shadow-[0_10px_30px_-10px_rgba(0,240,255,0.5)] hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white transition"
          data-testid="brain-setup-tile-continue"
        >
          {cta}
        </button>
      </div>
    </div>
  );
}
